import { SyncPortError } from '../../../shared/errors.js';
import type { FileMeta } from '../../../shared/transfer.js';

/** Where the receiver writes incoming bytes. Chosen once per transfer from the browser's capabilities. */
export type SinkKind = 'file-system-access' | 'service-worker' | 'memory';

export type SaveOutcome =
  /** Bytes already reached disk. `file` is available when the API lets us read the result back. */
  | Readonly<{ kind: 'written'; location: 'chosen-folder' | 'downloads'; savedName: string; file: File | null }>
  /** Bytes are held in a Blob and still need a user gesture to be saved. */
  | Readonly<{ kind: 'in-memory'; blob: Blob }>;

export type FileSink = Readonly<{
  kind: SinkKind;
  write: (chunk: ArrayBuffer) => Promise<void>;
  close: () => Promise<SaveOutcome>;
  abort: (reason: string) => Promise<void>;
}>;

export type SinkFactory = (meta: FileMeta) => Promise<FileSink>;

/** The user closed the destination picker; the transfer has not started and can be retried. */
export class SaveCancelledError extends SyncPortError {}

export type SinkCapabilities = Readonly<{
  hasSaveFilePicker: boolean;
  hasControllingWorker: boolean;
  /** WebKit browsers (Safari, every browser on iOS) are not verified for worker streaming yet. */
  webkit: boolean;
}>;

export const isWebKit = (userAgent: string): boolean =>
  /iPhone|iPad|iPod/i.test(userAgent) ||
  (/AppleWebKit/i.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|OPR|SamsungBrowser|Firefox|FxiOS/i.test(userAgent));

export const pickSinkKind = (capabilities: SinkCapabilities): SinkKind => {
  if (capabilities.hasSaveFilePicker) {
    return 'file-system-access';
  }
  if (capabilities.hasControllingWorker && !capabilities.webkit) {
    return 'service-worker';
  }
  return 'memory';
};

/** Content-Disposition with an ASCII fallback and an RFC 5987 encoded original name. */
export const contentDisposition = (fileName: string): string => {
  const ascii = fileName.replace(/[^\x20-\x7e]/g, '_').replace(/["\\]/g, '_');
  const encoded = encodeURIComponent(fileName).replace(/['()*]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);
  return `attachment; filename="${ascii}"; filename*=UTF-8''${encoded}`;
};
